import { useEffect, useState, useCallback } from "react";
import { X, Search, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const FOLDER_LABELS = {
  drivers: "Fahrer",
  teams: "Teams",
  race_screenshots: "Race Screenshots",
  news: "News",
  news_categories: "News-Kategorien",
  race_reports: "Rennberichte",
  "driver-cards": "Driver Cards (Archiv)",
};

const cldThumb = (url) => {
  if (!url || !url.includes("/upload/")) return url;
  return url.replace("/upload/", "/upload/f_auto,q_auto,w_300,h_200,c_fill/");
};

const fmtBytes = (b) => {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

export default function MediaLibrary({ open, onClose, onSelect, defaultFolder = null }) {
  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState(defaultFolder || "");
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ max_results: "200" });
      if (folder) params.set("folder", folder);
      const { data } = await api.get(`/cloudinary/library?${params}`);
      setItems(data.items || []);
      setFolders(data.folders || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mediathek konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [open, folder]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (open) setFolder(defaultFolder || ""); }, [open, defaultFolder]);

  if (!open) return null;

  const filtered = query.trim()
    ? items.filter(i => (i.public_id || "").toLowerCase().includes(query.toLowerCase()))
    : items;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="media-library-overlay"
    >
      <div
        className="card-surface max-w-6xl w-full max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
        data-testid="media-library"
      >
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <ImageIcon className="w-5 h-5 text-cyan-400" />
            <div className="font-display font-black italic uppercase text-lg">Bild-Mediathek</div>
            <span className="text-xs text-neutral-500">{filtered.length} Bild{filtered.length === 1 ? "" : "er"}</span>
          </div>
          <button onClick={onClose} className="btn-ghost text-xs" data-testid="media-library-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 p-4 border-b border-white/10">
          <button
            type="button"
            onClick={() => setFolder("")}
            className="text-[10px] uppercase tracking-widest px-2 py-1 border transition-colors"
            style={folder === "" ? { borderColor: "#00E5FF", background: "rgba(0,229,255,0.1)", color: "#00E5FF" } : { borderColor: "rgba(255,255,255,0.15)", color: "#a3a3a3" }}
            data-testid="media-library-folder-all"
          >Alle</button>
          {folders.map(f => (
            <button
              key={f}
              type="button"
              onClick={() => setFolder(f)}
              className="text-[10px] uppercase tracking-widest px-2 py-1 border transition-colors"
              style={folder === f ? { borderColor: "#00E5FF", background: "rgba(0,229,255,0.1)", color: "#00E5FF" } : { borderColor: "rgba(255,255,255,0.15)", color: "#a3a3a3" }}
              data-testid={`media-library-folder-${f}`}
            >{FOLDER_LABELS[f] || f}</button>
          ))}
          <div className="flex items-center gap-2 ml-auto bg-[#0B0F19] border border-white/10 px-2">
            <Search className="w-3.5 h-3.5 text-neutral-500" />
            <input
              type="text"
              placeholder="Suche nach Name…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              className="bg-transparent text-xs py-1 outline-none w-48"
              data-testid="media-library-search"
            />
          </div>
        </div>

        <div className="flex-1 overflow-auto p-4">
          {loading && <div className="text-neutral-500 text-sm">Lädt…</div>}
          {!loading && filtered.length === 0 && (
            <div className="text-neutral-500 text-sm italic text-center py-12">
              {query.trim() ? "Keine Treffer für die Suche." : "Keine Bilder in diesem Ordner."}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {filtered.map(i => (
              <button
                key={i.public_id}
                type="button"
                onClick={() => { onSelect(i.url); onClose(); }}
                className="group relative aspect-[3/2] bg-[#0B0F19] border border-white/10 overflow-hidden hover:border-cyan-400 transition-colors"
                data-testid={`media-library-item-${i.public_id}`}
                title={i.public_id}
              >
                <img src={cldThumb(i.url)} alt={i.public_id} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="absolute bottom-0 left-0 right-0 p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="text-[10px] text-white truncate">{(i.public_id || "").split("/").pop()}</div>
                  <div className="text-[9px] text-neutral-400 flex justify-between">
                    <span>{i.width}×{i.height}</span>
                    <span>{fmtBytes(i.bytes)}</span>
                  </div>
                </div>
                <span className="absolute top-1 left-1 text-[9px] uppercase tracking-widest px-1 py-0.5 bg-black/70 text-cyan-300">
                  {FOLDER_LABELS[i.folder] || i.folder}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
